import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, MapPin, Wifi, Images } from 'lucide-react';
import SmartImage from './SmartImage';
import ShareButton from './ShareButton';
import { programShareText } from '../lib/shareText';
import { todRange } from '../lib/datetime';
import { mediaUrl } from '../lib/api';

const fmtDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return null; }
};
const fmtTime = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }); }
  catch { return null; }
};
const sameDay = (a, b) => { try { return new Date(a).toDateString() === new Date(b).toDateString(); } catch { return false; } };
const tzLabel = (tz) => {
  if (!tz) return '';
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
      .formatToParts(new Date()).find((x) => x.type === 'timeZoneName');
    return part ? part.value : tz;
  } catch { return tz; }
};

export const ProgramCard = ({ p }) => {
  const img = mediaUrl(p.thumbnail_url || p.featured_image_url);
  return (
    <Link to={`/programs/${p.slug}`} className="group bg-white/70 rounded-3xl overflow-hidden border border-rose/40 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 block h-full">
      <SmartImage src={img} alt={p.title} className="aspect-[4/3]">
        <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5">
          <span className="rounded-full bg-cream/90 text-mahogany text-[11px] uppercase tracking-widest px-3 py-1 capitalize">{p.type}</span>
          {(p.mode === 'online' || p.mode === 'hybrid') && (
            <span className="rounded-full bg-burgundy/90 text-cream text-[11px] uppercase tracking-widest px-2.5 py-1 inline-flex items-center gap-1"><Wifi className="w-3 h-3" /> {p.mode === 'hybrid' ? 'Hybrid' : 'Online'}</span>
          )}
        </div>
        <div className="absolute top-3 right-3 z-20" onClick={(e) => e.preventDefault()}>
          <ShareButton compact url={`${window.location.origin}/programs/${p.slug}`} title={p.title} text={programShareText(p)} testid={`share-event-${p.slug}`} />
        </div>
        {p.start_datetime && (
          <div className="absolute bottom-3 left-3 z-10 rounded-full bg-mahogany text-cream text-xs px-4 py-2 shadow">{fmtDate(p.start_datetime)}</div>
        )}
        {p.gallery_urls?.length > 0 && (
          <div className="absolute bottom-3 right-3 z-10 rounded-full bg-cream/90 text-mahogany text-xs px-3 py-1.5 shadow inline-flex items-center gap-1.5" data-testid={`photo-badge-${p.slug}`}>
            <Images className="w-3.5 h-3.5" /> {p.gallery_urls.length} photos
          </div>
        )}
      </SmartImage>
      <div className="p-6">
        <div className="text-sm text-mahogany/70 mb-1">{p.pricing_type === 'enquiry' ? 'Registration by phone' : p.pricing_type === 'donation' ? `Suggested donation ${p.currency === 'inr' ? '₹' : '$'}${p.suggested_donation}` : p.pricing_type === 'per_day' ? `${p.currency === 'inr' ? '₹' : '$'}${p.per_day_price} / day` : p.pricing_type === 'flat_or_per_day' ? `From ${p.currency === 'inr' ? '₹' : '$'}${p.per_day_price} / day` : p.pricing_type === 'tickets' ? ((p.ticket_types || []).length ? `From ${p.currency === 'inr' ? '₹' : '$'}${Math.min(...p.ticket_types.map((t) => t.price || 0))}` : 'Tickets') : (p.is_free ? 'Community-Supported' : `${p.currency === 'inr' ? '₹' : '$'}${p.price}`)}</div>
        <h3 className="font-serif-display text-burgundy text-2xl leading-tight mb-3 group-hover:text-rose-italic transition-colors">{p.title}</h3>
        {p.short_description && <p className="text-sm text-burgundy/80 line-clamp-2 mb-3">{p.short_description}</p>}
        <div className="flex flex-col gap-1.5 text-sm text-burgundy/80">
          {p.type === 'class' && p.time_of_day && (
            <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-rose-italic" /> {todRange(p.time_of_day, p.duration_minutes)}</div>
          )}
          {p.start_datetime && (
            <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-rose-italic" /> {fmtDate(p.start_datetime)}{fmtTime(p.start_datetime) ? `, ${fmtTime(p.start_datetime)}` : ''}{p.end_datetime ? (sameDay(p.start_datetime, p.end_datetime) ? ` – ${fmtTime(p.end_datetime)}` : ` – ${fmtDate(p.end_datetime)}, ${fmtTime(p.end_datetime)}`) : ''}{p.timezone ? ` ${tzLabel(p.timezone)}` : ''}</div>
          )}
          {(p.location || p.mode === 'online') && (
            <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-rose-italic" /> {p.mode === 'online' ? 'Online' : p.location}</div>
          )}
        </div>
      </div>
    </Link>
  );
};

export default ProgramCard;
